import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthProvider'
import { useTreePermissions } from './useTreePermissions'
import { TREE_PERMISSION } from '../utils/treePermissions'
import { detectCityFromURL } from '../utils/cityDetector'
import { buildCantonUrl } from '../utils/cantonUrl'
import { useCities } from '../context/CitiesContext'
import * as RT from '../config/routeType'

// Permiso que exige la ruta (null: ruta no protegida)
const getRoutePermission = pathname => {
  if (pathname.includes(RT.REGISTER_TREE)) return TREE_PERMISSION.REGISTER
  if (pathname.includes(RT.MANAGEMENT_TREE)) return TREE_PERMISSION.UPDATE
  return null
}

/**
 * Hook que restringe el acceso a rutas de edición/registro
 * según el permiso de la sesión en el cantón: registrar-arbol exige registrar,
 * editar-arbol exige actualizar.
 *
 * - Admin: siempre permitido
 * - Rutas no protegidas (vista de mapa): siempre permitido
 * - Ruta protegida sin el permiso en el cantón → redirige al cantón preferido para ese permiso
 * - Sin el permiso en ningún cantón → redirige a vista del cantón (sin edición)
 */
export const useTenantGuard = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { isAuthenticated, loading } = useAuth()
  const { canActInCanton, getPreferredCantonSlug } = useTreePermissions()
  const { getEnabledCantones } = useCities()
  const cantones = getEnabledCantones()

  const currentCantonSlug = detectCityFromURL(pathname)
  const routePermission = getRoutePermission(pathname)

  const allowed = !routePermission || canActInCanton(routePermission, currentCantonSlug)

  useEffect(() => {
    if (loading || !isAuthenticated) return
    if (allowed) return

    // Sin el permiso de la ruta en este cantón: al cantón preferido, manteniendo el tipo de ruta
    const preferredCantonSlug = getPreferredCantonSlug(routePermission)
    if (preferredCantonSlug && preferredCantonSlug !== currentCantonSlug) {
      const routeSuffix =
        routePermission === TREE_PERMISSION.REGISTER ? `/${RT.REGISTER_TREE}` : `/${RT.MANAGEMENT_TREE}`
      navigate(buildCantonUrl(preferredCantonSlug, cantones, routeSuffix), { replace: true })
    } else {
      // Sin el permiso en ningún cantón → vista del cantón actual (solo lectura)
      navigate(buildCantonUrl(currentCantonSlug || RT.DEFAULT_CITY, cantones), { replace: true })
    }
  }, [
    loading,
    isAuthenticated,
    allowed,
    routePermission,
    getPreferredCantonSlug,
    cantones,
    navigate,
    currentCantonSlug,
  ])

  return { allowed, loading }
}
